import {ConfigService, Tractor, Vehicle, VehicleSettings} from 'shared';
import {ModelsList, StoreService} from 'shared';
import {Observable} from 'rxjs';
import {HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ErrorsService, Route, RouteParams, RouteLoader, FlowMeterDataLoader, FlowMeterBatchDataLoader, FlowMeterData} from 'shared';
import {map} from 'rxjs/operators';
import {decode} from '@msgpack/msgpack';
import {FlowMeterRequest} from '../fuel/flow-meter/flow-meter-request';

@Injectable({
  deps: [HttpClient],
  providedIn: 'root'
})
export class TractorService extends StoreService<Tractor> {


  constructor(http: HttpClient, config: ConfigService, errors: ErrorsService) {
    super(http, config, errors);
    this.initStore(Tractor);
  }

  protected load(queryParams?: HttpParams): Observable<ModelsList<Tractor>> {
    return this.http.get<ModelsList<Tractor>>(this.config.apiVerUrl('/app/tractors'), {params: queryParams});
  }

  public route(loader: RouteLoader, id, from: Date, to: Date, params: RouteParams): Observable<RouteLoader> {
    const requestData = {from, to};
    loader.isLoaded = false;
    loader.startLoading();
    Object.assign(requestData, params);
    return this.http.post(this.config.apiVerUrl(`/app/tractors/${id}/route`), requestData, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders(
        {'Content-Type': 'application/json'},
      )
    }).pipe(
      map<HttpEvent<ArrayBuffer>, RouteLoader>((e: HttpEvent<ArrayBuffer>) => {
        if (e.type === HttpEventType.Response) {
          loader.route = new Route(decode(e.body));
          loader.isLoaded = true;
          loader.endLoading();
        } else if (e.type === HttpEventType.DownloadProgress) {
          loader.isLoaded = false;
          loader.total = e.total;
          loader.loaded = e.loaded;
        }
        return loader;
      })
    );
  }

  public fuel(loader: FlowMeterDataLoader, id, from: Date, to: Date): Observable<FlowMeterDataLoader> {
    loader.isLoaded = false;
    loader.startLoading();
    return this.http.post(this.config.apiVerUrl(`/app/tractors/${id}/fuel`), {from, to}, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders(
        {'Content-Type': 'application/json'},
      )
    }).pipe(
      map<HttpEvent<ArrayBuffer>, FlowMeterDataLoader>((e: HttpEvent<ArrayBuffer>) => {
        if (e.type === HttpEventType.Response) {
          loader.data = new FlowMeterData(decode(e.body));
          loader.isLoaded = true;
          loader.endLoading();
        } else if (e.type === HttpEventType.DownloadProgress) {
          loader.isLoaded = false;
          loader.total = e.total;
          loader.loaded = e.loaded;
        }
        return loader;
      })
    );
  }

  public fuelBatch(loader: FlowMeterBatchDataLoader, ids: number[], from: Date, to: Date): Observable<FlowMeterBatchDataLoader> {
    loader.isLoaded = false;
    loader.startLoading();
    return this.http.post(this.config.apiVerUrl(`/app/tractors/fuel-batch`), {ids, from, to}, {
      responseType: 'arraybuffer',
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders(
        {'Content-Type': 'application/json'},
      )
    }).pipe(
      map<HttpEvent<ArrayBuffer>, FlowMeterBatchDataLoader>((e: HttpEvent<ArrayBuffer>) => {
        if (e.type === HttpEventType.Response) {
          const all = decode(e.body) as any[];
          loader.data = all.map(item => new FlowMeterData(item));
          loader.isLoaded = true;
          loader.endLoading();
        } else if (e.type === HttpEventType.DownloadProgress) {
          loader.isLoaded = false;
          loader.total = e.total;
          loader.loaded = e.loaded;
        }
        return loader;
      })
    );
  }

  protected insert(values: Tractor): Observable<number> {
    return this.http.post<number>(this.config.apiVerUrl('/app/tractors'), values);
  }

  protected remove(id): Observable<void> {
    return this.http.delete<void>(this.config.apiVerUrl(`/app/tractors/${id}`));
  }

  protected update(id, values): Observable<Tractor> {
    return this.http.put<Tractor>(this.config.apiVerUrl(`/app/tractors/${id}`), values);
  }

  public checkCode(code: string, id?: number): Observable<boolean> {
    return this.http.post<boolean>(this.config.apiVerUrl('/app/tractors/check-code'), {code, id});
  }

  public saveSettings(vehicle: Vehicle): Observable<VehicleSettings> {
    return this.http.post<VehicleSettings>(this.config.apiVerUrl(`/app/tractors/${vehicle.id}/save-settings`), vehicle.settings);
  }

}
